import clsx from 'clsx';
import { VpAsset, VpIcon, type VpAssetName } from '@vtmn-play/react';
import { useState, useRef, useMemo, type CSSProperties } from 'react';
import { countries } from './InputPhone.conf';
import FocusTrap from '../FocusTrap';
import { keyboardNav } from './InputPhone.utils';
import { v4 as uuidv4 } from 'uuid';
import { isValidLocale } from '@repo/common/helpers';

import './InputPhone.scss';

type InputPhoneProps = {
  defaultCountry: string;
  name: string;
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  size?: 'sm' | 'md' | 'lg';
  locale: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
};

export default function InputPhone({
  defaultCountry,
  name,
  value,
  onChange,
  onBlur,
  size = 'md',
  locale,
  placeholder,
  disabled = false,
  error = false,
}: Readonly<InputPhoneProps>) {
  const inputPhoneId = uuidv4();
  const countryList = useRef<HTMLUListElement | null>(null);
  const [country, setCountry] = useState(
    countries?.find(({ code }) => code === defaultCountry.toUpperCase()) ??
      undefined,
  );
  const indicator = useMemo(() => country?.phone || 'GB', [country]);

  const phoneNumber = useMemo(
    () => value?.replace(indicator, '').replace(/\s/g, ''),
    [value],
  );

  const onBeforeToggle = (
    e: React.SyntheticEvent<HTMLUListElement> & { newState?: string },
  ) => {
    if (e.newState === 'open') {
      globalThis.addEventListener('keyup', keyboardNav);
    }
    if (e.newState === 'closed') {
      globalThis.removeEventListener('keyup', keyboardNav);
    }
  };

  const syntheticEvent = (value: string) =>
    ({
      target: { value: value },
    }) as React.ChangeEvent<HTMLInputElement>;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhoneNumber = e.target.value;
    onChange(syntheticEvent(`${indicator}${newPhoneNumber}`));
  };

  const handleIndicatorChange = (
    code: string,
    country: string,
    phone: string,
  ) => {
    setCountry({ code, country, phone });
    countryList.current?.hidePopover();
    onChange(syntheticEvent(`${phone}${phoneNumber}`));
  };

  const countryDisplayNames = useMemo(() => {
    const validLocale = isValidLocale(locale) ? locale : 'en';
    if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
      return new Intl.DisplayNames([validLocale], { type: 'region' });
    }
    return null;
  }, [locale]);

  const getCountryName = (code: string, fallback: string) =>
    countryDisplayNames?.of(code) || fallback;

  return (
    <div
      className={clsx(`input-phone--size-${size} input-phone`, {
        'input-phone--disabled': disabled,
        'input-phone--error': error,
      })}
      style={{ anchorName: `--${inputPhoneId}` } as CSSProperties}
    >
      {country && (
        <>
          <button
            type='button'
            className='input-phone__country__button'
            popoverTarget={`${inputPhoneId}-country`}
          >
            <VpAsset
              name={`flag-${country.code.toLowerCase()}` as VpAssetName}
              className='rounded'
            />
            <VpIcon name='select' size={12} />
          </button>
          <ul
            ref={countryList}
            onBeforeToggle={onBeforeToggle}
            popover='auto'
            role='tooltip'
            aria-label={defaultCountry}
            aria-live='assertive'
            id={`${inputPhoneId}-country`}
            className='input-phone__country__list'
            style={{ positionAnchor: `--${inputPhoneId}` } as CSSProperties}
          >
            <FocusTrap>
              {countries?.map(({ code, country, phone }) => (
                <li key={code}>
                  <button
                    type='button'
                    className='input-phone__country__list__item'
                    onClick={() => handleIndicatorChange(code, country, phone)}
                  >
                    <span className='input-phone__country__list__item__flag'>
                      <VpAsset
                        name={`flag-${code.toLowerCase()}` as VpAssetName}
                        className='rounded'
                      />
                    </span>
                    <span className='input-phone__country__list__item__label'>
                      {getCountryName(code, country)}
                    </span>
                    <span className='input-phone__country__list__item__code'>
                      {phone}
                    </span>
                  </button>
                </li>
              ))}
            </FocusTrap>
          </ul>
          {country?.phone && (
            <>
              <span className='input-phone__country__code'>
                {country.phone}
              </span>
              <input
                type='hidden'
                value={indicator}
                name={name ? `${name}-phone-code` : 'phone-code'}
              />
            </>
          )}
        </>
      )}
      <input
        type='number'
        id={`${inputPhoneId}-input`}
        className='input-phone__field'
        name={name}
        value={phoneNumber}
        onChange={handlePhoneChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}
